import React from "react";
import { TextField, TextFieldProps } from "@mui/material";

type InputDecimalPtBrProps = Omit<TextFieldProps, "value" | "onChange" | "type"> & {
  value?: number;
  onChange: (valor: number | undefined) => void;
};

/**
 * FASE 05 — 2ª validação/PO 2026-07-24 (F5): campo decimal no padrão pt-BR.
 *
 * Aceita digitação com vírgula (e ponto, convertido para vírgula), inclusive "0,123"
 * (zero antes da vírgula) e o estado intermediário "0," durante a digitação. Mantém um
 * texto local para não perder a vírgula ao pressionar teclas, e propaga o número parseado
 * (ou undefined quando vazio) via onChange. Padrão reutilizável para os decimais do MOF novo.
 */
export const InputDecimalPtBr: React.FC<InputDecimalPtBrProps> = ({
  value,
  onChange,
  inputProps,
  ...rest
}) => {
  const formatar = (v?: number) =>
    v !== undefined && v !== null ? String(v).replace(".", ",") : "";

  const [texto, setTexto] = React.useState<string>(formatar(value));

  // Sincroniza o texto quando o valor externo muda e não corresponde ao já digitado
  // (ex.: ao carregar um cadastro existente). Evita sobrescrever o "0," em digitação.
  React.useEffect(() => {
    const atual = texto === "" || texto === "," ? undefined : parseFloat(texto.replace(",", "."));
    if (value !== atual) {
      setTexto(formatar(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Mantém apenas dígitos e separador decimal; ponto vira vírgula; só uma vírgula.
    let raw = e.target.value.replace(/[^0-9.,]/g, "").replace(/\./g, ",");
    const partes = raw.split(",");
    if (partes.length > 2) raw = partes[0] + "," + partes.slice(1).join("");
    setTexto(raw);

    const parsed = raw === "" || raw === "," ? undefined : parseFloat(raw.replace(",", "."));
    onChange(parsed !== undefined && Number.isNaN(parsed) ? undefined : parsed);
  };

  return (
    <TextField
      {...rest}
      value={texto}
      onChange={handleChange}
      inputProps={{ inputMode: "decimal", ...inputProps }}
    />
  );
};
